import { computed, onBeforeUnmount, ref } from 'vue'
import {
  MODULE_SHELL_MESSAGE_SOURCE,
  SYSTEM_PERMISSIONS,
  type ApiClient,
  type CreateSaasNotificationRequest,
  type ModuleShellMessage,
  type ModuleShellRegistration,
  type SaasNotification
} from '@xjlv2/api-client'
import type { ModuleMenuItem, ModuleNotificationPayload, ShellNotification, ShellNotificationTarget } from '../types'

interface NotificationOptions {
  createApiClient: () => ApiClient
  hasPermission: (permission: string) => boolean
  resolveModuleMenu: (target: ShellNotificationTarget) => ModuleMenuItem | null
  openTarget: (target: ShellNotificationTarget) => void
  onError?: (message: string) => void
}

export interface NotificationListQuery {
  type?: string
  read?: boolean | null
  pageNo?: number
  pageSize?: number
}

interface NotificationPage {
  records: SaasNotification[]
  total: number
  pageNo: number
  pageSize: number
}

export function asUnreadCount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.trunc(n)
}

export function useNotificationCenter(options: NotificationOptions) {
  const notifications = ref<ShellNotification[]>([])
  const notificationTotal = ref(0)
  const notificationPage = ref(1)
  const notificationPageSize = ref(20)
  const notificationLoading = ref(false)
  const notificationError = ref('')
  const notificationFilters = ref({ type: '', read: null as boolean | null })
  const registrations = ref<Record<string, ModuleShellRegistration>>({})
  const unreadCount = ref(0)
  const canReadNotifications = computed(() => options.hasPermission(SYSTEM_PERMISSIONS.notificationRead))
  const canManageNotifications = computed(() => options.hasPermission(SYSTEM_PERMISSIONS.notificationManage))

  async function loadNotifications(query: NotificationListQuery = {}) {
    if (!canReadNotifications.value) {
      notifications.value = []
      notificationTotal.value = 0
      unreadCount.value = 0
      return
    }
    const pageNo = query.pageNo ?? notificationPage.value
    const pageSize = query.pageSize ?? notificationPageSize.value
    const type = query.type ?? notificationFilters.value.type
    const read = query.read === undefined ? notificationFilters.value.read : query.read
    const params = new URLSearchParams({ pageNo: String(pageNo), pageSize: String(pageSize) })
    if (type) params.set('type', type)
    if (read !== null) params.set('read', String(read))
    notificationLoading.value = true
    notificationError.value = ''
    try {
      const [page, unread] = await Promise.all([
        options.createApiClient().get<NotificationPage>(`/api/saas/system/notifications?${params}`),
        options.createApiClient().get<number>('/api/saas/system/notifications/unread-count')
      ])
      notifications.value = page.records.map(toShellNotification)
      notificationTotal.value = page.total
      notificationPage.value = page.pageNo
      notificationPageSize.value = page.pageSize
      notificationFilters.value = { type, read }
      unreadCount.value = asUnreadCount(unread)
    } catch (err) {
      notificationError.value = err instanceof Error ? err.message : '通知加载失败'
      throw err
    } finally {
      notificationLoading.value = false
    }
  }

  async function pushNotification(payload: ModuleNotificationPayload) {
    if (!canManageNotifications.value) {
      return null
    }
    const request = toCreateNotificationRequest(payload)
    const item = toShellNotification(
      await options.createApiClient().post<SaasNotification>('/api/saas/system/notifications', request)
    )
    await loadNotifications({ pageNo: 1 })
    return item
  }

  async function openNotification(notification: ShellNotification) {
    await markRead(notification.id)
    if (hasTarget(notification.target)) {
      options.openTarget(notification.target)
    }
  }

  async function markRead(id: number) {
    if (!canManageNotifications.value) {
      return
    }
    await options.createApiClient().put<SaasNotification>(`/api/saas/system/notifications/${id}/read`)
    await loadNotifications()
  }

  async function markAllRead() {
    if (!canManageNotifications.value) {
      return
    }
    await options.createApiClient().put<number>('/api/saas/system/notifications/read-all')
    await loadNotifications()
  }

  async function removeNotification(id: number) {
    if (!canManageNotifications.value) {
      return
    }
    await options.createApiClient().delete<void>(`/api/saas/system/notifications/${id}`)
    await loadNotifications()
  }

  async function clearNotifications() {
    if (!canManageNotifications.value) {
      return
    }
    await options.createApiClient().delete<number>('/api/saas/system/notifications')
    await loadNotifications({ pageNo: 1 })
  }

  async function handleWindowMessage(event: MessageEvent) {
    const data = event.data as Partial<ModuleShellMessage>
    if (!data || data.source !== MODULE_SHELL_MESSAGE_SOURCE) return
    if (data.type === 'notify' && data.payload?.title) {
      try {
        await pushNotification(data.payload as ModuleNotificationPayload)
      } catch (err) {
        options.onError?.(err instanceof Error ? err.message : '通知写入失败')
      }
      return
    }
    if (data.type === 'open' && data.target) {
      options.openTarget(data.target as ShellNotificationTarget)
      return
    }
    if (data.type === 'register' && data.registration?.moduleCode) {
      registerModule(data.registration)
    }
  }

  function registerModule(registration: ModuleShellRegistration) {
    registrations.value = {
      ...registrations.value,
      [registration.moduleCode]: registration
    }
  }

  function toCreateNotificationRequest(payload: ModuleNotificationPayload): CreateSaasNotificationRequest {
    const registration = payload.moduleCode ? registrations.value[payload.moduleCode] : undefined
    const target = payload.target ?? (registration?.target as ShellNotificationTarget | undefined) ?? {}
    const targetMenu = options.resolveModuleMenu(target)
    const moduleCode = payload.moduleCode ?? registration?.moduleCode ?? target.moduleCode ?? targetMenu?.moduleCode ?? ''
    const moduleName = payload.moduleName ?? registration?.moduleName ?? targetMenu?.moduleName ?? (moduleCode || 'SaaS Shell')
    return {
      title: payload.title,
      body: payload.body ?? '',
      type: payload.type ?? 'INFO',
      sender: payload.sender ?? registration?.sender ?? moduleName,
      moduleCode,
      moduleName,
      target,
      linkText: payload.linkText ?? targetMenu?.name ?? (target.tab ? '打开页面' : '')
    }
  }

  window.addEventListener('message', handleWindowMessage)
  onBeforeUnmount(() => window.removeEventListener('message', handleWindowMessage))

  return {
    notifications,
    notificationTotal,
    notificationPage,
    notificationPageSize,
    notificationLoading,
    notificationError,
    unreadCount,
    canReadNotifications,
    canManageNotifications,
    loadNotifications,
    pushNotification,
    openNotification,
    markRead,
    markAllRead,
    removeNotification,
    clearNotifications,
    registerModule
  }
}

function toShellNotification(notification: SaasNotification): ShellNotification {
  return {
    ...notification,
    type: notification.type ?? 'INFO',
    sender: notification.sender || notification.moduleName || 'SaaS Shell',
    moduleCode: notification.moduleCode || '',
    moduleName: notification.moduleName || '',
    target: (notification.target ?? {}) as ShellNotificationTarget,
    linkText: notification.linkText || ''
  }
}

function hasTarget(target: ShellNotificationTarget) {
  return Boolean(target.tab || target.permissionSection || target.menuCode || target.moduleCode || target.path)
}
